import React, { useMemo } from 'react';
import { Field, useFormik } from 'formik';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { useIntl } from 'react-intl';
import {
	Button,
	Div,
	InputGroup,
	Select,
	Typography,
} from '@icecreamsocial/components';
import { ChonkyInput } from '../ChonkyInput';
import { Wizard } from '../Wizard';
import { PhoneNumberInput } from './PhoneNumberInput';
import { CountryCodeSelect } from './CountryCodeSelect';
import { useAtom } from 'jotai';
import {
	anyConsentRequiredAtom,
	requiredConsentAtom,
} from '../../../shared/atoms';

/**
 * @todo turn this into a container component
 */

export const ContactCollection = ({
	unlockLabel = 'Activate Offer',
	continueLabel = 'Continue',
	useSMS = false,
	privacyURL,
	defaultEmail,
	defaultMobileNumber,
	onSubmit,
}) => {
	const [anyConsentRequired] = useAtom(anyConsentRequiredAtom);
	const [requireConsent] = useAtom(requiredConsentAtom);

	const initialValues = {
		email: defaultEmail,
		mobileNumber: defaultMobileNumber,
		emailConsent: false,
		smsConsent: false,
	};

	const inputs = [
		(props) => (
			<InputGroup {...props}>
				<InputGroup.Label
					display='flex'
					alignItems='center'
					justifyContent='space-between'
				>
					Your email address{' '}
					{useSMS && <Typography.Span as='i'>Optional</Typography.Span>}
				</InputGroup.Label>
				<InputGroup.Fields paddingLeft='md'>
					<ChonkyInput
						as={Field}
						type='email'
						name='email'
						defaultValue={defaultEmail}
						required={!useSMS}
					/>
				</InputGroup.Fields>
			</InputGroup>
		),
		(props) => (
			<InputGroup {...props}>
				<InputGroup.Label
					display='flex'
					alignItems='center'
					justifyContent='space-between'
				>
					Your phone number{' '}
					{!useSMS && <Typography.Span as='i'>Optional</Typography.Span>}
				</InputGroup.Label>

				<InputGroup.Fields overflow='visible' alignItems='center'>
					<CountryCodeSelect
						name='country'
						defaultCountry={'US'}
						boxShadows='none'
						border='0'
						padding='md'
					/>
					<PhoneNumberInput
						as={Field}
						type='tel'
						name='mobileNumber'
						required={useSMS}
						country={props.formik.values.country?.abbreviation}
						defaultValue={defaultMobileNumber}
					/>
				</InputGroup.Fields>
				{props.formik?.errors.mobileNumber ? (
					<Typography.Span display='block' textColor='bad' marginTop='sm'>
						{props.formik.errors.mobileNumber}
					</Typography.Span>
				) : null}
			</InputGroup>
		),
	];

	const checkboxes = [
		({ formik, ctx, ...props }) =>
			formik.values.email ? (
				<InputGroup {...props}>
					<Checkbox
						name='emailConsent'
						color='primary'
						value={formik.values.emailConsent}
						checked={formik.values.emailConsent === true}
						onClick={formik.handleChange}
						required={requireConsent.email}
					/>
					<InputGroup.Label
						title={`Email updates to ${formik.values.email}`}
						overflow='hidden'
						style={{ textOverflow: 'ellipsis' }}
					>
						Email updates to{' '}
						<Typography.Span textDecoration='underline'>
							{formik.values.email}
						</Typography.Span>
					</InputGroup.Label>
					<Button
						type='button'
						padding='sm'
						onClick={() => ctx.back(formik.values)}
					>
						Edit
					</Button>
				</InputGroup>
			) : null,
		({ formik, ctx, ...props }) =>
			formik.values.mobileNumber ? (
				<InputGroup {...props}>
					<Checkbox
						name='smsConsent'
						color='primary'
						value={formik.values.smsConsent}
						checked={formik.values.smsConsent === true}
						onClick={formik.handleChange}
						required={requireConsent.sms}
					/>
					<InputGroup.Label
						title={`Text updates to ${formik.values.mobileNumber}`}
						overflow='hidden'
						style={{ textOverflow: 'ellipsis' }}
					>
						Text updates to{' '}
						<Typography.Span textDecoration='underline'>
							{formik.values.mobileNumber}
						</Typography.Span>
					</InputGroup.Label>
					<Button
						type='button'
						padding='sm'
						onClick={() => ctx.back(formik.values)}
					>
						Edit
					</Button>
				</InputGroup>
			) : null,
	];

	// want the prioritized input to be displayed first
	const orderedInputs = useSMS ? [...inputs].reverse() : [...inputs];

	const orderedConsent = useSMS ? [...checkboxes].reverse() : [...checkboxes];

	return (
		<Wizard
			initialValues={initialValues}
			onSubmit={onSubmit}
			lastStepLabel={unlockLabel}
			continueLabel={continueLabel}
		>
			<Wizard.Step>
				{(formik) => (
					<>
						{orderedInputs.map((Input, key) => (
							<Input
								key={key}
								formik={formik}
								elWidth='100%'
								marginBottom='md'
							/>
						))}
						<Typography.P typoSize='.8em'>
							We protect your data with care, just as described in our{' '}
							<Typography.A target='_blank' href={privacyURL}>
								Privacy Policy
							</Typography.A>
							.
						</Typography.P>
					</>
				)}
			</Wizard.Step>
			{anyConsentRequired ? (
				<Wizard.Step>
					{(formik, ctx) => (
						<>
							{orderedConsent.map((Consent, key) => (
								<Consent
									formik={formik}
									ctx={ctx}
									key={key}
									grid
									gridColumns='auto 10fr auto'
									alignItems='center'
									elWidth='100%'
									marginBottom='md'
								/>
							))}
							<Typography.P typoSize='.8em' marginTop='lg'>
								We'll contact you here with offer and reward updates.
								Unsubscribe anytime.
							</Typography.P>
							<Typography.P typoSize='.8em'>
								We protect your data with care, just as described in our{' '}
								<Typography.A target='_blank' href={privacyURL}>
									Privacy Policy
								</Typography.A>
								.
							</Typography.P>
						</>
					)}
				</Wizard.Step>
			) : null}
		</Wizard>
	);
};
